"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListChecks,
  Code,
  Quote,
  Table,
  Minus,
  CheckSquare,
  Image as ImageIcon,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface SlashCommand {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  insert: string;
}

export const SLASH_COMMANDS: SlashCommand[] = [
  { id: "h1", label: "Encabezado 1", description: "Título principal (#)", icon: Heading1, insert: "# " },
  { id: "h2", label: "Encabezado 2", description: "Subtítulo (##)", icon: Heading2, insert: "## " },
  { id: "h3", label: "Encabezado 3", description: "Sub-subtítulo (###)", icon: Heading3, insert: "### " },
  { id: "ul", label: "Lista", description: "Lista con viñetas (-)", icon: List, insert: "- " },
  { id: "ol", label: "Lista numerada", description: "Lista numerada (1.)", icon: ListChecks, insert: "1. " },
  { id: "task", label: "Tarea", description: "Checkbox de tarea (- [ ])", icon: CheckSquare, insert: "- [ ] " },
  { id: "code", label: "Bloque de código", description: "Code fence (```)", icon: Code, insert: "```\n\n```" },
  { id: "quote", label: "Cita", description: "Bloque de cita (>)", icon: Quote, insert: "> " },
  { id: "table", label: "Tabla", description: "Tabla Markdown", icon: Table, insert: "| Col 1 | Col 2 |\n| --- | --- |\n| ... | ... |" },
  { id: "hr", label: "Separador", description: "Línea horizontal (---)", icon: Minus, insert: "\n---\n" },
  { id: "image", label: "Imagen", description: "Insertar imagen (![]()", icon: ImageIcon, insert: "![alt](url)" },
];

interface SlashCommandMenuProps {
  /** Texto después del último `/` en la línea actual. */
  query: string;
  /** Posición del caret en viewport (para colocar el menú). */
  position: { x: number; y: number } | null;
  /** Callback al seleccionar un comando. */
  onSelect: (command: SlashCommand) => void;
  /** Cierra el menú (Escape, click fuera, etc). */
  onClose: () => void;
  /** Index inicial seleccionado. */
  initialIndex?: number;
}

/**
 * Menú flotante de comandos que aparece al escribir `/` en línea vacía.
 * Filtrable por texto, navegable con ↑/↓/Enter/Esc.
 * Se renderiza en un portal para escapar overflow:hidden.
 */
export function SlashCommandMenu({
  query,
  position,
  onSelect,
  onClose,
  initialIndex = 0,
}: SlashCommandMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const filtered = query
    ? SLASH_COMMANDS.filter(
        (c) =>
          c.label.toLowerCase().includes(query.toLowerCase()) ||
          c.id.toLowerCase().includes(query.toLowerCase()),
      )
    : SLASH_COMMANDS;

  // Reset index si los resultados cambian
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Listener global de teclado para navegar el menú
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((i) => (i + 1) % Math.max(filtered.length, 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((i) => (i - 1 + filtered.length) % Math.max(filtered.length, 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        const cmd = filtered[selectedIndex];
        if (cmd) onSelect(cmd);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [filtered, selectedIndex, onSelect, onClose]);

  // Click fuera cierra
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  if (!mounted || !position || filtered.length === 0) return null;

  return createPortal(
    <div
      ref={ref}
      role="listbox"
      style={{ left: position.x, top: position.y }}
      className="fixed z-[250] w-72 rounded-xl border border-border/60 bg-popover/95 backdrop-blur-md shadow-xl p-1.5 animate-in fade-in zoom-in-95 duration-100"
    >
      <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 mb-1">
        Comandos {query && `(${filtered.length})`}
      </div>
      <div className="max-h-72 overflow-y-auto custom-scrollbar">
        {filtered.map((cmd, i) => {
          const Icon = cmd.icon;
          return (
            <button
              key={cmd.id}
              role="option"
              aria-selected={i === selectedIndex}
              onMouseEnter={() => setSelectedIndex(i)}
              onClick={() => onSelect(cmd)}
              className={cn(
                "w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left transition-colors",
                i === selectedIndex
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-secondary/60",
              )}
            >
              <Icon
                className={cn(
                  "w-3.5 h-3.5 shrink-0",
                  i === selectedIndex ? "text-primary-foreground" : "text-muted-foreground/70",
                )}
                strokeWidth={1.75}
              />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium truncate">{cmd.label}</div>
                <div
                  className={cn(
                    "text-[10px] truncate",
                    i === selectedIndex ? "text-primary-foreground/80" : "text-muted-foreground/60",
                  )}
                >
                  {cmd.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}
